// Robust microphone acquisition. macOS continuity devices (a nearby iPhone's
// microphone) can be handed out as Chrome's DEFAULT audio input and die the
// moment the track is created (readyState "ended", zero signal) — the user
// sees a granted permission, a selected built-in mic in Chrome settings, and a
// silent recording. Detect the dead default and retry once with an explicitly
// chosen healthy input. Verified live (2026-07-09): default → "iPhone
// Microphone" ended / 0.0 rms; explicit Built-in → live / 0.12 rms.

export interface AudioInputInfo {
  kind: string;
  deviceId: string;
  label: string;
}

/**
 * Pick a concrete alternative audio input after `deadLabel` failed, preferring
 * the built-in microphone. Virtual "default"/"communications" ids are skipped —
 * they alias the very default that just died.
 */
export function pickAlternativeInput(devices: AudioInputInfo[], deadLabel: string): AudioInputInfo | undefined {
  const candidates = devices.filter(
    (d) =>
      d.kind === "audioinput" &&
      d.deviceId.length > 0 &&
      d.deviceId !== "default" &&
      d.deviceId !== "communications" &&
      d.label !== deadLabel
  );
  return candidates.find((d) => /built-in|内建|内置/i.test(d.label)) ?? candidates[0];
}

/**
 * Is this track dead? Two signatures of an unavailable continuity device:
 * the track ends asynchronously right after acquisition, OR it stays "live"
 * but delivers PURE digital zeros (a real microphone always has a noise
 * floor > 0, so exact-zero peak over the probe window means no device behind
 * the track). Probes for ~600ms via a throwaway AudioContext.
 */
async function trackIsDead(stream: MediaStream, track: MediaStreamTrack): Promise<boolean> {
  if (track.readyState === "ended") return true;
  try {
    const ctx = new AudioContext();
    const an = ctx.createAnalyser();
    an.fftSize = 512;
    ctx.createMediaStreamSource(stream).connect(an);
    const buf = new Uint8Array(an.fftSize);
    let peak = 0;
    const t0 = Date.now();
    while (Date.now() - t0 < 600) {
      an.getByteTimeDomainData(buf);
      for (const v of buf) peak = Math.max(peak, Math.abs((v - 128) / 128));
      await new Promise((r) => setTimeout(r, 50));
    }
    await ctx.close();
    if (track.readyState !== "live") return true;
    return peak === 0; // literal digital silence = dead device, not a quiet room
  } catch {
    return track.readyState !== "live"; // no AudioContext — only trust readyState
  }
}

/**
 * getUserMedia({audio:true}) with a dead-default fallback. A dead default
 * track (see trackIsDead) is dropped and ONE alternative device is tried; if
 * none exists the original stream is returned and the caller's level meter
 * surfaces the silence. Throws only when getUserMedia itself rejects
 * (permission denied).
 */
/** Persisted user mic choice ("" / absent = system default). Continuity
 * devices can stay "live" while delivering near-silence (an iPhone in another
 * room), which no automatic dead-check can distinguish from a quiet room — an
 * explicit, persisted user choice is the only robust cure. */
export const MIC_DEVICE_STORAGE_KEY = "cyclecare_mic_device";

export function preferredMicId(): string {
  try { return localStorage.getItem(MIC_DEVICE_STORAGE_KEY) ?? ""; } catch { return ""; }
}

export function setPreferredMicId(id: string): void {
  try {
    if (id.length > 0) localStorage.setItem(MIC_DEVICE_STORAGE_KEY, id);
    else localStorage.removeItem(MIC_DEVICE_STORAGE_KEY);
  } catch { /* storage unavailable — choice just won't persist */ }
}

export async function getMicStream(): Promise<MediaStream> {
  // an explicit user choice wins over everything (falls through if the device
  // disappeared — unplugged USB mic etc.)
  const preferred = preferredMicId();
  if (preferred.length > 0) {
    try {
      return await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: preferred } } });
    } catch { /* stale device id — fall back to the default path below */ }
  }
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const track = stream.getAudioTracks()[0];
  if (track === undefined || !(await trackIsDead(stream, track))) return stream;
  track.stop();
  const devices = await navigator.mediaDevices.enumerateDevices();
  const alt = pickAlternativeInput(devices, track.label);
  if (alt === undefined) return stream;
  return navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: alt.deviceId } } });
}
